import { gql } from "@apollo/client";
import { useMutation, useQuery } from "@apollo/client/react";
import { DSButton } from "@components/design-system/molecules";
import { faSave } from "@fortawesome/free-regular-svg-icons";
import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

import Title from "./Title";
import HeadingWrapper from "../common/HeadingWrapper";
import FourohFour from "../home/FourohFour";
import PageTitle from "../layout/PageTitle";
import { KeyPatientInfoFormFields } from "../notes/edit/form/KeyPatientInfoFormFields";
import { EDIT_PATIENT_MUTATION } from "../patients/api/mutations";

const GET_PATIENT_KEY_INFO = gql`
  query patientKeyInfo($uuid: String) {
    patient(uuid: $uuid) {
      uuid
      name
      lastNameOfOwner
      species
      sex
      breed
      age
      keyHistory
      keyMedications
      vaccinationStatus
      weight
    }
  }
`;

export default function KeyPatientInfo() {
  const { patientUUID } = useParams();
  const navigate = useNavigate();
  const { control, handleSubmit, reset } = useForm();

  const { data, loading } = useQuery(GET_PATIENT_KEY_INFO, {
    variables: {
      uuid: patientUUID,
    },
    fetchPolicy: "no-cache",
  });

  const [editPatient] = useMutation(EDIT_PATIENT_MUTATION);

  useEffect(() => {
    if (data && data.patient) {
      const {
        keyHistory,
        keyMedications,
        vaccinationStatus,
        weight,
      } = data.patient;

      reset({
        keyHistory,
        keyMedications,
        vaccinationStatus,
        weight: weight == "Not recorded" ? "" : weight,
      });
    }
  }, [data, reset]);

  if (!data && loading) {
    return null;
  }

  if (!data && !loading) {
    return <FourohFour />;
  }

  function onSubmit(data) {
    const { keyHistory, keyMedications, vaccinationStatus, weight } =
      data;

    editPatient({
      variables: {
        patientUuid: patientUUID,
        patientInput: {
          keyHistory,
          keyMedications,
          vaccinationStatus,
          weight,
        },
      },
    });
    navigate(-1);
  }

  const {
    name,
    keyHistory,
    keyMedications,
    vaccinationStatus,
    weight,
  } = data.patient;

  return (
    <div>
      <PageTitle title="Key Patient Info" />
      <div className="my-1 py-8 ">
        <HeadingWrapper
          heading={<Title patient={data?.patient} />}
        ></HeadingWrapper>

        <h5 className="text-secondary text-sm">
          Key information added here will be shown at the top of{" "}
          {name}&apos;s notebook. You&apos;ll see it alongside key
          info gathered from {name}&apos;s notes.
        </h5>
      </div>

      <KeyPatientInfoFormFields
        control={control}
        keyHistory={keyHistory || ""}
        keyMedications={keyMedications || ""}
        vaccinationStatus={vaccinationStatus || ""}
        weight={weight}
        placeholder="Type key info here..."
      />

      <div className="flex flex-row justify-center my-md space-x-4">
        <DSButton
          variant="cancel"
          size="lg"
          onClick={() => navigate(-1)}
          dataTestId="cancel-key-info"
        >
          Cancel
        </DSButton>
        <DSButton
          variant="primary"
          size="lg"
          icon={faSave}
          onClick={handleSubmit(onSubmit)}
          dataTestId="save-key-info"
        >
          Save Key Info
        </DSButton>
      </div>
    </div>
  );
}
